import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

// Phase A (drawer) of the training-task comment section. Talks to the Phase 2
// backend already merged in Portal-BFF (/task-comment/portal/*). The points
// economy (paying the commenter per like) and the completed-task badge land in
// later phases — this service is just comments + like/unlike.
export const taskCommentApi = createApi({
  reducerPath: "taskCommentApi",
  tagTypes: ["TaskComments"],
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // Returns { comments, canComment, canModerate, viewerRecid }.
    getTaskComments: builder.query({
      query: (taskTypeRecid) =>
        `/task-comment/portal/get-comments?taskTypeRecid=${encodeURIComponent(taskTypeRecid)}`,
      transformResponse: (resp) => resp?.data || {},
      providesTags: (result, error, taskTypeRecid) => [
        { type: "TaskComments", id: taskTypeRecid },
      ],
    }),

    // Phase C — the comment author's completed submission for the task the
    // comment lives on. Fetched lazily when a viewer taps the "completed" badge
    // on a name. Returns { authorName, taskTypeName, completedDate, notes,
    // instructions, proofLinks[], proofImages[], brilliumUrl }.
    getAuthorSubmission: builder.query({
      query: (commentId) =>
        `/task-comment/portal/get-author-submission?commentId=${encodeURIComponent(commentId)}`,
      transformResponse: (resp) => resp?.data || {},
    }),

    addTaskComment: builder.mutation({
      query: ({ taskTypeRecid, body }) => ({
        url: "/task-comment/portal/add-comment",
        method: "POST",
        body: { taskTypeRecid, body },
      }),
      invalidatesTags: (result, error, { taskTypeRecid }) => [
        { type: "TaskComments", id: taskTypeRecid },
      ],
    }),

    editTaskComment: builder.mutation({
      query: ({ commentId, body }) => ({
        url: "/task-comment/portal/edit-comment",
        method: "POST",
        body: { commentId, body },
      }),
      invalidatesTags: (result, error, { taskTypeRecid }) => [
        { type: "TaskComments", id: taskTypeRecid },
      ],
    }),

    // liked=true likes, liked=false unlikes. Response data carries
    // { commentId, liked, likeCount, pointsDelta, pointsPerLike } — pointsDelta
    // is the signed amount the author's balance just moved (drives the float).
    setTaskCommentLike: builder.mutation({
      query: ({ commentId, liked }) => ({
        url: "/task-comment/portal/set-like",
        method: "POST",
        body: { commentId, liked },
      }),
      invalidatesTags: (result, error, { taskTypeRecid }) => [
        { type: "TaskComments", id: taskTypeRecid },
      ],
    }),

    deleteTaskComment: builder.mutation({
      query: ({ commentId }) => ({
        url: "/task-comment/portal/delete-comment",
        method: "POST",
        body: { commentId },
      }),
      invalidatesTags: (result, error, { taskTypeRecid }) => [
        { type: "TaskComments", id: taskTypeRecid },
      ],
    }),
  }),
});

export const {
  useGetTaskCommentsQuery,
  useGetAuthorSubmissionQuery,
  useAddTaskCommentMutation,
  useEditTaskCommentMutation,
  useSetTaskCommentLikeMutation,
  useDeleteTaskCommentMutation,
} = taskCommentApi;
